import React, { useState, useEffect } from 'react'
import { Modal, Button, InputNumber, message, Collapse } from 'antd'
import { CaretRightOutlined, PrinterOutlined } from '@ant-design/icons'
import { ApiRequest } from '../../../../services/api/api'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import { modalStyles, titleStyle, buttonStyles, modalContainerStyle, tableBorder, tableCellStyle, tableHeaderStyle } from './modal-styles'
import palletSticker from '../../../static/pdf/pallet-stickers/pallet-sticker'

interface BoxDetail {
  boxId: string
  barCode: string
  status: number
  statusText: string
  plateId?: number
  plateCode?: string
  location?: string
  isOnPallet: boolean
  isDispatched: boolean
  productId?: number
}

interface InspectionItemDetail {
  rawMaterialId: number
  itemCode: string
  description: string
  revAreaQty: number  // 接收区域数量
  putAwayQty: number  // 上架数量
}

// API 返回的托盘结果
interface PalletResultDto {
  plateId: number
  plateCode: string
  itemCode: string
  description: string
  qty: number
  boxes: BoxDetail[]
}

interface SaveSplitPalletsResultDto {
  pallets: PalletResultDto[]
}

interface PalletItem {
  palletNumber: string        // 临时托盘号（Pallet 1, Pallet 2...）或实际托盘号
  itemCode: string
  quantity: number
  originalQuantity: number
  plateId?: number            // 保存后的实际托盘ID
  plateCode?: string          // 保存后的实际托盘号
  boxIds?: string[]           // 该托盘包含的 box-id 列表（字符串类型）
  boxes?: BoxDetail[]         // 该托盘包含的 box 详细信息
}

interface AutoSplitSuccessModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  itemData: any
  palletData: PalletItem[]
  userPalletStacking: number  // 从 AutoSplitPalletModal 传入的用户输入值
  onSuccess: () => void
}

const AutoSplitSuccessModal: React.FC<AutoSplitSuccessModalProps> = ({
  visible,
  onClose,
  poData,
  itemData,
  palletData,
  userPalletStacking,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaved, setIsSaved] = useState(false)  // 是否已保存
  const [palletList, setPalletList] = useState<PalletItem[]>([])
  const [palletStacking, setPalletStacking] = useState<number>(userPalletStacking || 100)  // 使用传入的值
  const [palletStackingName, setPalletStackingName] = useState<string>('')
  const [itemDetail, setItemDetail] = useState<InspectionItemDetail | null>(null)
  const [inspectionId, setInspectionId] = useState<number | null>(null)

  // 提取的公共函数：获取 inspectionId
  const fetchInspectionId = async (): Promise<number | null> => {
    let id = poData?.inspectionId || itemData?.inspectionId
    if (!id) {
      const inspectionResponse = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })
      if (inspectionResponse?.data?.data && inspectionResponse.data.data.length > 0) {
        id = inspectionResponse.data.data[0]?.inspectionId
      }
    }
    setInspectionId(id)
    return id
  }

  // 获取 Inspection Item 详情（从 unloadingDetail 获取）
  const fetchItemDetail = async () => {
    try {
      const inspectionResponse = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (inspectionResponse?.data?.data && inspectionResponse.data.data.length > 0) {
        const inspectionData = inspectionResponse.data.data[0]

        // 从 unloadingDetail 查找对应的物料
        const foundDetail = (inspectionData.unloadingDetail || []).find((detail: any) =>
          detail.rawMaterialId === itemData.rawMaterial?.rawMaterialId
        )

        if (foundDetail) {
          const detail = {
            rawMaterialId: foundDetail.rawMaterialId,
            itemCode: itemData.rawMaterial?.rawMaterialCode || '',
            description: itemData.rawMaterial?.rawMaterialName || '',
            revAreaQty: foundDetail.revAreaQty || 0,
            putAwayQty: foundDetail.putAwayQty || 0
          }
          setItemDetail(detail)
          return detail
        }
      }
    } catch (error) {
      // Silently handle fetch errors
    }
    return null
  }

  // 提取的公共函数：获取 Pallet Stacking 信息
  const fetchPalletStacking = async (): Promise<{ quantity: number; name: string; ctnsPerLevel?: number; level?: number; addOnQty?: number }> => {
    try {
      const rawMaterialResponse = await ApiRequest({
        url: `RawMaterial/GetRawMaterialById?id=${itemData.rawMaterial?.rawMaterialId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (rawMaterialResponse?.data?.data) {
        const rawMaterialData = rawMaterialResponse.data.data
        const productId = rawMaterialData.productId

        if (productId) {
          const productResponse = await ApiRequest({
            url: `Product/GetProductById?id=${productId}`,
            method: 'get',
            isShowSpinner: false
          })

          if (productResponse?.data?.data?.palletStacking) {
            const palletStackingData = productResponse.data.data.palletStacking
            return {
              quantity: palletStackingData.quantity || 100,
              name: palletStackingData.palletStackingName || '',
              ctnsPerLevel: palletStackingData.ctnsPerLevel,
              level: palletStackingData.level,
              addOnQty: palletStackingData.addOnQty
            }
          }
        }
      }
    } catch (error) {
      // Handle fetch errors silently
    }
    return { quantity: 100, name: '' }
  }

  // 生成托盘分组（基于 Receiving Area Qty）
  const generatePalletGroups = (stackingQty: number, receivingQty: number): PalletItem[] => {
    const totalPallets = Math.ceil(receivingQty / stackingQty)
    const generatedPallets: PalletItem[] = []

    for (let i = 0; i < totalPallets; i++) {
      const remainingQty = receivingQty - (i * stackingQty)
      const qty = Math.min(remainingQty, stackingQty)
      generatedPallets.push({
        palletNumber: `Pallet ${i + 1}`,
        itemCode: itemData?.rawMaterial?.rawMaterialCode || '',
        quantity: qty,
        originalQuantity: qty
      })
    }

    return generatedPallets
  }

  // 获取 Receiving Area 数据并生成托盘分组
  useEffect(() => {
    if (visible && itemData) {
      fetchReceivingAreaDataAndGeneratePallets()
    }
  }, [visible, itemData])

  // 获取 Receiving Area 数据并生成托盘分组
  const fetchReceivingAreaDataAndGeneratePallets = async () => {
    try {
      // 重置保存状态
      setIsSaved(false)
      setHasUnsavedChanges(false)

      const detail = await fetchItemDetail()

      if (!detail || detail.revAreaQty <= 0) {
        setPalletList([])
        return
      }

      // 使用传入的 userPalletStacking 而不是重新获取
      setPalletStacking(userPalletStacking)

      // 可选：仍然获取 palletStackingName 用于显示
      try {
        const stackingInfo = await fetchPalletStacking()
        setPalletStackingName(stackingInfo.ctnsPerLevel && stackingInfo.level
          ? `${stackingInfo.ctnsPerLevel}*${stackingInfo.level}+${stackingInfo.addOnQty || 0}`
          : stackingInfo.name)
      } catch (e) {
        setPalletStackingName('')
      }

      const generatedPallets = generatePalletGroups(userPalletStacking, detail.revAreaQty)
      setPalletList(generatedPallets)
    } catch (error) {
      // 使用默认值生成
      const defaultStackingQty = 100
      setPalletStacking(defaultStackingQty)
      const generatedPallets = generatePalletGroups(defaultStackingQty, 0)
      setPalletList(generatedPallets)
    }
  }

  // 更新数量
  const handleQuantityChange = (index: number, value: number) => {
    const newPalletList = [...palletList]
    newPalletList[index].quantity = value || 0
    setPalletList(newPalletList)
    setHasUnsavedChanges(true)
  }

  // 计算总数量
  const getTotalQuantity = () => {
    return palletList.reduce((sum, item) => sum + (item.quantity || 0), 0)
  }

  // 获取原始总数量（Receiving Area Qty）
  const getOriginalTotalQuantity = () => {
    return itemDetail?.revAreaQty || 0
  }

  // 保存数据
  const handleSave = async () => {
    // 校验数量总和 - 使用 itemDetail.revAreaQty
    const totalQty = getTotalQuantity()
    const originalTotal = itemDetail?.revAreaQty || 0

    if (totalQty !== originalTotal) {
      message.error(`Total quantity (${totalQty}) must equal Receiving Area Qty (${originalTotal})`)
      return
    }

    setLoading(true)

    try {
      // 使用公共函数获取 inspectionId
      const id = await fetchInspectionId()

      if (!id) {
        message.warning('Cannot get Inspection ID, please complete receiving process first')
        return
      }

      // 准备提交数据 - 只传入数量，不传入 boxIds
      const saveData = {
        poDetailId: itemData.poDetailId,
        pallets: palletList.map(pallet => ({
          quantity: pallet.quantity,
          plateTypeId: 2  // 临时托盘类型
        }))
      }

      // 调用保存 API
      const response = await ApiRequest({
        url: 'PurchaseOrder/SaveSplitPallets',
        method: 'post',
        data: saveData,
        isShowSpinner: true
      })

      await SweetAlertService.successMessage('Pallet data saved successfully')

      // 更新托盘列表为实际托盘信息
      if (response?.data?.data) {
        const result: SaveSplitPalletsResultDto = response.data.data
        const updatedPalletList = palletList.map((pallet, index) => {
          const palletResult = result.pallets?.[index]
          return {
            ...pallet,
            plateId: palletResult?.plateId,
            plateCode: palletResult?.plateCode || pallet.palletNumber,
            boxIds: palletResult?.boxes?.map((b: any) => b.boxId) || [],
            boxes: palletResult?.boxes || []
          }
        })
        setPalletList(updatedPalletList)
      }

      // 重新获取 Item 详情以更新 Putaway Qty
      await fetchItemDetail()

      setIsSaved(true)
      setHasUnsavedChanges(false)
      onSuccess()
    } catch (error) {
      message.error('Save failed')
    } finally {
      setLoading(false)
    }
  }

  // 获取 logo base64
  const fetchLogoBase64 = async (): Promise<string> => {
    try {
      const response = await fetch('/logo.png')
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      return ''
    }
  }

  // 关闭操作
  const handleClose = () => {
    if (hasUnsavedChanges && !isSaved) {
      SweetAlertService.confirmMessage('Data not saved, are you sure you want to close?').then((result) => {
        if (result) {
          setIsSaved(false)
          setHasUnsavedChanges(false)
          onClose()
        }
      })
    } else {
      setIsSaved(false)
      setHasUnsavedChanges(false)
      onClose()
    }
  }

  // 打印所有托盘 barcode
  const handlePrintAllPalletBarcodes = () => {
    if (isSaved && palletList.length > 0) {
      const palletCodes = palletList
        .map(pallet => pallet.plateCode)
        .filter((code): code is string => Boolean(code) && typeof code === 'string' && code.length > 0)

      if (palletCodes.length > 0) {
        palletSticker(palletCodes, 'print')
      } else {
        message.warning('No valid pallet codes to print')
      }
    } else {
      message.warning('Please save pallet data before printing')
    }
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleClose}
      width={1000}
      footer={null}
      closable={true}
      style={{ top: 50 }}
      bodyStyle={{ padding: modalStyles.padding, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          Auto Split Success
        </div>

        {/* 提示文本 */}
        <div style={{
          textAlign: 'center',
          fontSize: '14px',
          color: modalStyles.textSecondary,
          marginBottom: '16px',
          fontWeight: 500
        }}>
          All product has been split to the following pallets:
        </div>

        {/* Pallet Stacking 信息 */}
        {palletStackingName && (
          <div style={{
            textAlign: 'center',
            fontSize: '14px',
            color: modalStyles.textPrimary,
            marginBottom: '16px',
            padding: '8px 16px',
            backgroundColor: '#f0fdf4',
            border: `1px solid ${modalStyles.success}`,
            borderRadius: modalStyles.borderRadiusSmall,
            display: 'inline-block',
            marginLeft: '50%',
            transform: 'translateX(-50%)'
          }}>
            <strong>Pallet Stacking:</strong> {palletStackingName} ({palletStacking} cartons/pallet)
          </div>
        )}

        {/* 物料数量信息 */}
        {itemDetail && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#eff6ff',
              border: `1px solid ${modalStyles.info}`,
              borderRadius: modalStyles.borderRadiusSmall,
              fontSize: '14px',
              color: modalStyles.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontWeight: 500 }}>Receiving Area Qty:</span>
              <span style={{ fontWeight: 600, color: modalStyles.info }}>{itemDetail.revAreaQty}</span>
            </div>
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#fef3c7',
              border: `1px solid ${modalStyles.warning}`,
              borderRadius: modalStyles.borderRadiusSmall,
              fontSize: '14px',
              color: modalStyles.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontWeight: 500 }}>Putaway Qty:</span>
              <span style={{ fontWeight: 600, color: modalStyles.warning }}>{itemDetail.putAwayQty}</span>
            </div>
          </div>
        )}

        {/* 状态提示 */}
        <div style={{
          textAlign: 'center',
          fontSize: '13px',
          color: isSaved ? modalStyles.success : modalStyles.warning,
          marginBottom: '16px',
          fontWeight: 500
        }}>
          {isSaved ? '✓ Saved - Pallets Generated' : '⚠ Not Saved - Temporary Pallet Numbers'}
        </div>

        {/* 拆分结果列表 */}
        <div style={{ marginBottom: '20px' }}>
          <table style={{ width: '100%', ...tableBorder, borderRadius: modalStyles.borderRadiusSmall, overflow: 'hidden' }}>
            <thead>
              <tr style={tableHeaderStyle}>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Pallet Number</th>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Item code</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {palletList.map((item, index) => (
                <tr key={item.palletNumber} style={{ backgroundColor: index % 2 === 0 ? modalStyles.bgWhite : modalStyles.bgGray }}>
                  <td style={{ ...tableCellStyle, color: modalStyles.textPrimary, fontWeight: 500 }}>
                    {isSaved && item.plateCode ? (
                      <span style={{ color: modalStyles.success }}>{item.plateCode}</span>
                    ) : (
                      <span style={{ color: modalStyles.warning }}>{item.palletNumber}</span>
                    )}
                  </td>
                  <td style={{ ...tableCellStyle }}>
                    <div
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#eff6ff',
                        border: `2px solid ${modalStyles.info}`,
                        borderRadius: modalStyles.borderRadiusTiny,
                        color: '#1d4ed8',
                        fontSize: '14px',
                        fontWeight: 500,
                        textAlign: 'center'
                      }}
                    >
                      {item.itemCode}
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                    {isSaved ? (
                      <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.success }}>
                        {item.quantity}
                      </span>
                    ) : (
                      <InputNumber
                        size="small"
                        value={item.quantity}
                        onChange={(value) => handleQuantityChange(index, value || 0)}
                        min={0}
                        style={{ width: '100px' }}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Boxes 列表（可折叠） */}
          {isSaved && palletList.some(p => p.boxes && p.boxes.length > 0) && (
            <Collapse
              defaultActiveKey={[]}
              style={{
                marginTop: '16px',
                backgroundColor: modalStyles.bgWhite,
                border: `1px solid ${modalStyles.borderSecondary}`,
                borderRadius: modalStyles.borderRadiusSmall
              }}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
              {palletList.filter(p => p.boxes && p.boxes.length > 0).map((pallet, index) => (
                <Collapse.Panel
                  key={pallet.palletNumber}
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <span style={{ fontWeight: 500, color: modalStyles.textPrimary }}>
                        {pallet.plateCode || pallet.palletNumber} - Boxes ({pallet.boxes?.length || 0})
                      </span>
                    </div>
                  }
                >
                  <div style={{ padding: '12px 0' }}>
                    <table style={{ width: '100%', ...tableBorder, borderRadius: modalStyles.borderRadiusSmall, overflow: 'hidden' }}>
                      <thead>
                        <tr style={tableHeaderStyle}>
                          <th style={{ ...tableCellStyle, textAlign: 'left' }}>Barcode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pallet.boxes?.map((box, boxIndex) => (
                          <tr key={box.boxId} style={{ backgroundColor: boxIndex % 2 === 0 ? modalStyles.bgWhite : modalStyles.bgGray }}>
                            <td style={{ ...tableCellStyle }}>
                              <div style={{
                                padding: '6px 10px',
                                backgroundColor: '#f0fdf4',
                                border: `1px solid ${modalStyles.success}`,
                                borderRadius: modalStyles.borderRadiusTiny,
                                color: '#065f46',
                                fontSize: '13px',
                                fontWeight: 500,
                                textAlign: 'center',
                                fontFamily: 'monospace'
                              }}>
                                {box.barCode}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </div>

        {/* 保存按钮 */}
        {!isSaved && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <Button
              type="primary"
              onClick={handleSave}
              loading={loading}
              style={{
                ...buttonStyles.success,
                minWidth: '100px',
                lineHeight: '1.5',
                padding: '6px 16px',
                fontSize: '14px'
              }}
            >
              Save
            </Button>
          </div>
        )}

        {/* 底部操作按钮区 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: `2px solid ${modalStyles.borderSecondary}` }}>
          <Button
            onClick={handlePrintAllPalletBarcodes}
            icon={<PrinterOutlined />}
            disabled={!isSaved}
            style={{
              ...(isSaved ? buttonStyles.success : buttonStyles.secondary),
              minWidth: '160px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isSaved ? 1 : 0.5
            }}
            onMouseEnter={(e) => {
              if (isSaved) {
                e.currentTarget.style.backgroundColor = '#065f46'
                e.currentTarget.style.borderColor = '#065f46'
              }
            }}
            onMouseLeave={(e) => {
              if (isSaved) {
                e.currentTarget.style.backgroundColor = modalStyles.success
                e.currentTarget.style.borderColor = modalStyles.success
              }
            }}
          >
            Print All Pallet Barcodes
          </Button>
          <Button
            onClick={handleClose}
            style={{
              ...buttonStyles.secondary,
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = modalStyles.bgLight
              e.currentTarget.style.borderColor = modalStyles.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff'
              e.currentTarget.style.borderColor = modalStyles.primary
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AutoSplitSuccessModal
